import React, { useRef, useCallback, useState, useEffect } from 'react';
import { FiLogIn, FiMail } from 'react-icons/fi';
import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';
import { Link } from 'react-router-dom';

import { useToast } from '../../context/ToastContext';
import { useConfigs } from '../../context/ConfigsContext';
import { useIndexedDB } from '../../context/IndexedDBContext';
import api from '../../services/api';

import getValidationErrors from '../../utils/getValidationErros';

import logoImg from '../../assets/Sizex Clin Logo.png';

import Input from '../../components/input';
import Button from '../../components/button';

import { Container, Content, AnimationContainer, Background } from './styles';

interface ForgotPasswordFormData {
  email: string;
}

const ForgotPassword: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [empresa, setEmpresa] = useState<string>('');
  const formRef = useRef<FormHandles>(null);

  const { addToast } = useToast();
  const { cores } = useConfigs();
  const storage = useIndexedDB();

  useEffect(() => {
    async function loadEmpresa() {
      const empresaSalva = await storage.lerIndexedDB<string>('empresa');
      setEmpresa(empresaSalva || '');
    }
    loadEmpresa();
  }, [storage]);

  const handleSubmit = useCallback(async (data: ForgotPasswordFormData) => {
    try {
      setLoading(true);

      formRef.current?.setErrors({});

      const schema = Yup.object().shape({
        email: Yup.string().required('Email obrigatório').email('Digite um e-mail válido'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      await api.post('/password/forgot', {
        email: data.email,
      });

      addToast({
        type: 'success',
        title: 'E-mail de recuperação enviado',
        description: 'Enviamos um email para confirmar a recuperação de senha, cheque sua caixa de entrada.'
      });
    } catch (err) {
      if (err instanceof Yup.ValidationError) {
        const errors = getValidationErrors(err);
        formRef.current?.setErrors(errors);
        return;
      }

      addToast({
        type: 'error',
        title: 'Erro na recuperação de senha',
        description: 'Ocorreu um erro ao tentar realizar a recuperação de senha, tente novamente.',
      });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  return (
    <Container>
      <Content>
        <AnimationContainer cores={cores}>
          <img src={logoImg} alt="Logo Sizex" height={280} width={280} />

          <Form ref={formRef} onSubmit={handleSubmit}>
            <h1>Recuperar Senha</h1>

            <Input name="email" type="text" placeholder="E-mail" icon={FiMail} autoFocus />

            <Button loading={loading} type="submit">Recuperar</Button>
          </Form>

          <Link to={`/${empresa}`}>
            <FiLogIn />
            Voltar ao login
          </Link>
        </AnimationContainer>
      </Content>

      <Background />
    </Container>
  );
};

export default ForgotPassword;
